import { inject, Injectable } from "@angular/core";
import { StreamingHubService } from "./streaming-hub.service";
import { fromEvent, Observable } from "rxjs";
import { ClassroomCommand, CommandType } from "../interfaces/streaming";
import { filter } from "rxjs/operators";
import { SignalrLessonClient } from "../models/signalr-lesson-client";
import {
    CanvasInteractionEvent,
    DriverscamVideoMinimumInfo,
    Hotspot,
    IDisplaySlide,
    LiveSessionState,
    MinimumSlideInformation,
    QuestionIdentifier,
    SlideTypeCategory,
} from "@pcp/core/interfaces";
import { extractSlideTypeCategory } from "@pcp/core/util";
import {
    NavigateToPreviewDriverscamAssignmentCommand,
    NavigateToPreviewSlideCommand,
    SendActivateHotspotCommand,
    SendNavigateToParentCommand,
    SendRedoCommand,
    SendUndoCommand,
    ShowDifficultQuestionElementCommand,
    UpdateRubberElementCommand,
} from "../interfaces/slide-commands";
import { SeekVideoCommand, VideoCommand } from "../interfaces/video-commands";
import { PCP_LOGGER } from "@pcp/logging";


type SlideLayer = 1 | 2 | 3;


@Injectable({
    providedIn: 'root'
})
export class LessonHubFacade {

    private readonly streamingHub = inject(StreamingHubService);
    private readonly logger = inject(PCP_LOGGER).createScoped('signalr');

    private readonly connection = this.streamingHub.connection;


    /** Any user in the classroom has connected. */
    readonly onClassroomUserConnected$: Observable<SignalrLessonClient> = fromEvent(
        this.connection,
        'UserConnected'
    );

    /** Any user in the classroom has disconnected. */
    readonly onClassroomUserDisconnected$: Observable<SignalrLessonClient> = fromEvent(
        this.connection,
        'UserDisconnected'
    );

    /** Received a content-related Command */
    private readonly commandReceived$: Observable<ClassroomCommand> = fromEvent(
        this.connection,
        'Command'
    );

    readonly Command = this.commandReceived$;
    readonly onBeamerConnected$ = this.Command.pipe(filter(command => command.commandType === CommandType.BEAMER_IS_CONNECTED));

    /**
     * Receive LiveLesson state changes.
     * Contains the updated state as string.
     */
    readonly liveSessionStateCmd$: Observable<LiveSessionState> = fromEvent(
        this.connection,
        'SendLiveLessonState'
    );


    async sendStopLesson(): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendStopLesson');

        await this.streamingHub.sendStopLesson({
            commandParam: '',
            commandType: CommandType.STOP_LESSON,
        });
    }

    /**
     * Move mouse or erase (draw) something from the image.
     */
    async updateRubberElementContent(
        changes: CanvasInteractionEvent,
        slide: IDisplaySlide,
        layer: SlideLayer,
    ): Promise<void> {
        this.logger.verbose('ClassroomConnector::updateCanvasContent');

        const forElementType: SlideTypeCategory = extractSlideTypeCategory(slide);
        const cmd: UpdateRubberElementCommand = {
            changes: {
                action: changes.drag === true ? 'drawPoint' : 'move',
                type: 'eraser',
                x: changes.mousePosition.x,
                y: changes.mousePosition.y,
            },
            commandType: CommandType.UPDATE_CANVAS,
            commandParam: '',
            layer,
            forSlideId: slide.id,
            guid: slide.guid,
        };
        await this.streamingHub.sendCanvasContentUpdate(cmd);
    }

    /**
     * Add dynamic content to a slide.
     * E.g. add a bullet point to a textboard.
     *
     * @param slide The slide the dynamic content is appended to.
     * @param dynamicContentSlide The dynamic content to append.
     * @param layer The slide's layer (1|2|3)
     */
    /**
     * Hotspot has been clicked on a beamer:
     * Send command to teacher - the teacher app should activate this hotspot.
     */
    async sendActivateHotspotToTeacher(hotspot: Hotspot, slide: IDisplaySlide, layer: SlideLayer = 1): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendActivateHotspotToTeacher');

        const cmd: SendActivateHotspotCommand = {
            commandType: CommandType.ACTIVATE_HOTSPOT,
            commandParam: '',
            hotspot,
            forSlideId: slide.id,
        };
        await this.streamingHub.sendActivateHotspotCommand(cmd);
    }

    /**
     * Send Undo from Beamer to Teacher
     */
    async sendUndoCommandToTeacher(): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendUndoCommandToTeacher');

        const cmd: SendUndoCommand = {
            commandType: CommandType.UNDO,
            commandParam: '',
        };
        await this.streamingHub.sendUndoCommand(cmd);
    }

    /**
     * Send Undo from Beamer to Teacher
     */
    async sendRedoCommandToTeacher(): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendRedoCommandToTeacher');

        const cmd: SendRedoCommand = {
            commandType: CommandType.REDO,
            commandParam: '',
        };
        await this.streamingHub.sendRedoCommand(cmd);
    }

    async sendNavigateToParentCommandToTeacher(): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendNavigateToParentCommandToTeacher');

        const cmd: SendNavigateToParentCommand = {
            commandType: CommandType.NAVIGATE_TO_PARENT,
            commandParam: '',
        };
        await this.streamingHub.sendNavigateToParentCommand(cmd);
    }

    async sendVideoStartedPlaying(
        slide: IDisplaySlide,
        playbackStartPosition: number,
        layer: SlideLayer,
    ): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendVideoStartedPlaying');

        const command: VideoCommand = {
            commandType: CommandType.PLAY,
            commandParam: playbackStartPosition.toString(),
            layer,
            guid: slide.guid,
        };
        await this.streamingHub.sendVideoStartedPlaying(command);
    }

    async sendVideoPaused(slide: IDisplaySlide, layer: SlideLayer): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendVideoPaused');

        const command: VideoCommand = {
            commandType: CommandType.PAUSE,
            commandParam: '',
            layer,
            guid: slide.guid,
        };
        await this.streamingHub.sendVideoPaused(command);
    }

    async sendVideoLoaded(slide: IDisplaySlide, layer: SlideLayer): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendVideoLoaded');

        const command: VideoCommand = {
            commandType: CommandType.LOAD,
            commandParam: '',
            layer,
            guid: slide.guid,
        };
        await this.streamingHub.sendVideoLoaded(command);
    }

    async sendVideoSeeked(
        slide: IDisplaySlide,
        seekedToPosition: number,
        isInPausedState: boolean,
        layer: SlideLayer,
    ): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendVideoSeeked');

        const command: SeekVideoCommand = {
            commandType: CommandType.SEEK,
            commandParam: '',
            layer,
            seconds: seekedToPosition,
            isInPausedState,
            guid: slide.guid,
        };
        await this.streamingHub.sendVideoSeeked(command);
    }

    async sendNavigateToPreviewSlideToTeacher(slide: MinimumSlideInformation): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendNavigateToPreviewSlideToTeacher');

        const command: NavigateToPreviewSlideCommand = {
            commandType: CommandType.NAVIGATE_TO_PREVIEW_SLIDE,
            commandParam: '',
            slide,
        };
        await this.streamingHub.sendNavigateToPreviewSlide(command);
    }

    async sendNavigateToPreviewDriverscamAssignmentToTeacher(driverscam: DriverscamVideoMinimumInfo): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendNavigateToPreviewDriverscamAssignmentToTeacher');

        const command: NavigateToPreviewDriverscamAssignmentCommand = {
            commandType: CommandType.NAVIGATE_TO_PREVIEW_DRIVERSCAM_ASSIGNMENT,
            commandParam: '',
            driverscam,
        };
        await this.streamingHub.sendNavigateToPreviewDriverscamAssignment(command);
    }

    async sendShowDifficultQuestionElementToTeacher(difficultQuestionNumbers: QuestionIdentifier[]): Promise<void> {
        this.logger.verbose('ClassroomConnector::sendShowDifficultQuestionElementToTeacher');

        const command: ShowDifficultQuestionElementCommand = {
            commandType: CommandType.SHOW_DIFFICULT_QUESTION_ELEMENT,
            commandParam: '',
            difficultQuestionNumbers,
        };
        await this.streamingHub.sendShowDifficultQuestionElement(command);
    }

}
